import { h } from 'preact';
import Store from 'store/store';
import { post, request, getAllMedia } from 'utils/api';
import { isLocalMoviePage, save } from 'utils/data';
import * as jsonPointer from 'utils/jsonPointer';
import { Modal, showModal, toast } from 'utils/modal';
import { isFocusStudio } from 'utils/studioConfig';
import { xhr } from 'utils/xhr';

import { showUploadManager } from 'components/media/uploadManagerToast';
import Button from 'components/button/button';
import Cropper from 'components/media/cropper';

// queues up patch requests to be sent when user saves the page data
// to avoid sync issues if user assigns media files but closes without saving
const enqueue = (id, path, details) => {
	if (!Object.keys(details).length) return;
	Store.get().mediaUpdateQueue.push({ id, path, details });
};
const clearQueueForPath = (path) => {
	const queue = Store.get().mediaUpdateQueue;
	const newQueue = queue.filter(item => item.path !== path);
	if (newQueue.length !== queue.length) {
		Store.get().set('mediaUpdateQueue', newQueue);
	}
};

const justIds = (items) => {
	if (!items || !Array.isArray(items)) return [];
	return items.map(item => item.id);
};

export async function checkDimensionsAndAssignMedia(at, file, assetData) {
	const dimensionsWarning = checkMediaDimensions(file, assetData);
	if (file.media_type !== 'video' && dimensionsWarning) {
		const dimensions = assetData?.recommendedDimensions?.split(':').map(Number);
		const aspectRatioParts = assetData?.aspectRatio?.split(':');
		await showModal(Cropper, {
			file,
			assetData,
			aspectRatio: aspectRatioParts ? aspectRatioParts[0] / aspectRatioParts[1] : null,
			width: dimensions?.[0],
			height: dimensions?.[1],
			path: at
		});
	} else {
		await assignMedia(at, file);
	}
}

export async function assignMedia(at, file) {
	clearQueueForPath(at);

	// get section from folderPath
	const [topLevel, groupId] = jsonPointer.parse(at);

	const update = {};
	// Note: this will not work with navigation (which doesn't use assets at the moment, but good to keep in mind)
	if (topLevel === 'movies' && !file.movie.some(m => m.id === groupId)) {
		update.movie = justIds(file.movie).concat(groupId);
	}
	if (topLevel === 'pages' && !file.section.some(s => s.id === groupId)) {
		update.section = justIds(file.section).concat(groupId);
	}
	// queue patch to db to associate file with section
	enqueue(file.id, at, update);

	// ---- UPDATE 04/03/2026: We should not automatically unlink media from a movie (leaving code in but commented out) ----
	// deassociate previous file from its section
	// NOTE: we should check that it is not still used somewhere on the same page before doing this (a deep search for the url feels hacky but might work)
	// try {
	// 	const url = jsonPointer.get(Store.get().data, at);
	// 	if (url) {
	// 		const existing = await getMediaByURL(url);
	// 		existing.forEach(existingFile => {
	// 			const update = {};
	// 			if (topLevel === 'movies' && existingFile.movie.some(m => m.id === groupId)) {
	// 				update.movie = justIds(existingFile.movie).filter(id => id !== groupId);
	// 			}
	// 			if (topLevel === 'pages' && existingFile.section.some(s => s.id === groupId)) {
	// 				update.section = justIds(existingFile.section).filter(id => id !== groupId);
	// 			}
	// 			enqueue(existingFile.id, at, update);
	// 		});
	// 	}
	// } catch (err) {
	// 	console.error(err);
	// }

	// acually set the value
	jsonPointer.setDeep(Store.get().data, at, file.s3_url);
}

export async function downloadAll(item) {
	const idType = item.movie ? 'movieId' : 'sectionId';
	toast('Preparing media files for download...', { duration: item.movie ? 6000 : 3000 });
	const response = await post(
		'media/downloadAll',
		{
			[idType]: item.id
		}
	);
	const a = document.createElement('a');
	a.href = response.s3SignedUrl;
	a.download = item.name + '.zip';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
}

export async function getImageDimensions(file) {
	const isUrl = typeof file === 'string';
	return new Promise((resolve, reject) => {
		if (isUrl || file.type.includes('image')) {
			let img = new Image();
			img.onload = () => resolve({ width: img.width, height: img.height });
			img.onerror = reject;
			img.src = isUrl ? file : URL.createObjectURL(file);
		} else if (file.type.includes('video')) {
			let video = document.createElement('video');
			video.onloadedmetadata = () => {
				resolve({ width: video.videoWidth, height: video.videoHeight });
			};
			video.onerror = reject;
			video.src = URL.createObjectURL(file);
		} else {
			resolve(null);
		}
	});
}

function reduceFraction(num, denom) {
	let result = '';
	for (let i = Math.max(num, denom); i > 1; i--) {
		if ((num % i == 0) && (denom % i == 0)) {
			num /= i;
			denom /= i;
		}
	}
	if (denom === 1) {
		result = num.toString();
	} else {
		result = num.toString() + ':' + denom.toString();
	}
	return result;
}

function checkMediaDimensions(file, assetData) {
	if (!file || !assetData) return null;

	const { width, height } = file;
	if (!width || !height) return null;
	const { aspectRatio, recommendedDimensions } = assetData;

	// Check aspect ratio
	if (aspectRatio) {
		const [aspectWidth, aspectHeight] = aspectRatio.split(':');
		// TODO: allow being up to 1px off ?
		if (aspectWidth / aspectHeight !== width / height) {
			// warning that aspect ratio is off
			return `Aspect ratio of this media does not match the required aspect ratio. Expected ${aspectRatio} (${reduceFraction(aspectWidth, aspectHeight)}), but got ${width}:${height} (${reduceFraction(width, height)}). Your media may display at an incorrect resolution.`;
		}
	}

	// Check recommended dimensions
	// if (recommendedDimensions) {
	// 	const [recommendedWidth, recommendedHeight] = recommendedDimensions.split(':').map(Number);
	// 	if (width !== recommendedWidth || height !== recommendedHeight) {
	// 		// warning that width/height is off
	// 		return `Aspect ratio is correct but width/height is does not match the required width/height. Expected ${recommendedWidth}x${recommendedHeight}px, but got ${width}x${height}px. Your media may display at an unclear resolution.`;
	// 	}
	// }

	return null;
}

function getNewFileName(fileName, currentMedia) {
	const extension = fileName.split('.').pop();
	let baseName = fileName.split('.').slice(0, -1).join('.');
	let newFileName = `${baseName}(1).${extension}`;
	let iteration = 1;
	while (currentMedia.find(media => media.name === newFileName)) {
		newFileName = newFileName.replace(`(${iteration})`, `(${iteration + 1})`);
		iteration++;
	}
	return newFileName;
}

export async function onFileInput(files, group) {
	const filesArray = Array.from(files);

	const options = {};

	// Note: the file might not actually be used after it is uploaded, which means it is in a wrong state in the DB
	// TODO: either automatically (single-file upload)
	//   or do not pre-associate files here (but ensure the new files are displayed in the media selector)
	if (group) {
		const data = Store.get().data;
		if (data.movies && (group in data.movies)) {
			options.movieId = group;
		} else if (data.pages && (group in data.pages)) {
			options.sectionId = group;
		}
	}
	options.onProcessed = () => {
		Store.emit('UPDATE_MEDIA');
	};

	const uploads = filesArray.map(file => upload(file, options));
	// If the media selector is open, try to open the upload manager inside it (to allow user interaction, since the modal blocks interaction with the rest of the page)
	const mountPoint = document.querySelector('dialog#media-selector');
	showUploadManager(uploads, mountPoint);
	await Promise.allSettled(uploads.map(u => u.promise));
}

// An approach of getting image container to calculate object position based on focal point was used.
// It was necessary to ensure the focal point is actually centered in the image container
// Using the focal point as a direct object position percentage, meant if an image was to be left aligned the focal point would need to be fully on the left of the image
export function calcImagePositionForFocalPoint(imgWidth, imgHeight, containerWidth, containerHeight, focalX, focalY) {
	if (focalX == null || focalY == null || !containerWidth || !containerHeight || !imgWidth || !imgHeight) {
		return '50% 50%';
	}

	const containerAspect = containerWidth / containerHeight;
	const imageAspect = imgWidth / imgHeight;

	let xPercent = 50;
	let yPercent = 50;

	if (imageAspect > containerAspect) {
		// Image is wider - horizontal overflow
		const scaledWidth = containerHeight * imageAspect;
		const overflow = scaledWidth - containerWidth;
		const focalXScaled = focalX * scaledWidth;
		const offset = Math.max(0, Math.min(overflow, focalXScaled - containerWidth / 2));
		xPercent = overflow > 0 ? (offset / overflow) * 100 : 50;
	} else {
		// Image is taller - vertical overflow
		const scaledHeight = containerWidth / imageAspect;
		const overflow = scaledHeight - containerHeight;
		const focalYScaled = focalY * scaledHeight;
		const offset = Math.max(0, Math.min(overflow, focalYScaled - containerHeight / 2));
		yPercent = overflow > 0 ? (offset / overflow) * 100 : 50;
	}

	return `${xPercent}% ${yPercent}%`;
}

/**
 * JSON pointer (relative to the movie record) of the image used to auto-generate the share image,
 * or null when the share image is uploaded manually instead.
 */
export function getShareImageSource(movieData) {
	if (isFocusStudio()) return '/content/hero/still';
	if (isLocalMoviePage(movieData)) return '/content/hero/desktop/still';
	return null;
}

export async function createAndUploadShareImage(movieId) {
	const movieData = Store.get().data.movies?.[movieId];
	if (!movieData?.content) return false;

	const sourcePath = getShareImageSource(movieData);
	if (!sourcePath) return false;

	const heroStill = jsonPointer.get(movieData, sourcePath);
	if (!heroStill?.url) return false;

	// Generate share image with the same focal point as the hero still
	const imageBlob = await generateShareImage(heroStill.url, heroStill?.focalPoint);

	// Upload new share image
	const { id } = movieData.content.metaData?.shareImage || {};
	const file = new File([imageBlob], `${movieData.content.title}_share.jpg`, { type: 'image/jpeg' });
	const uploadResult = await new Promise((resolve, reject) => {
		const event = upload(file, { movieId, replaceId: id, onProcessed: resolve });

		// Check for errors and reject if any occur during the upload process
		const checkAndReject = () => {
			if (event.error) reject(new Error(event.error));
		};

		checkAndReject(); // Check for synchronous errors (e.g., file size validation)
		event.addEventListener('update', checkAndReject);
	});

	if (uploadResult?.error || !uploadResult?.url) throw new Error('Failed to upload share image');

	// Set share image data
	if (!movieData.content.metaData) jsonPointer.setDeep(Store.get().data, `/movies/${movieId}/content/metaData`, {});
	jsonPointer.setDeep(Store.get().data, `/movies/${movieId}/content/metaData/shareImage`, {
		url: uploadResult.url,
		id: uploadResult.id,
	});
	return true;
}

function generateShareImage(heroStillUrl, focalPoint) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onerror = err => reject(err);
		img.onload = async () => {
			const { focalX, focalY } = focalPoint || {};
			const { naturalWidth, naturalHeight } = img;
			const targetWidth = 1920;
			const targetHeight = 960;
			const targetAspect = targetWidth / targetHeight;
			const imageAspect = naturalWidth / naturalHeight;
			const maxSize = maxFileSize['image/*'];

			const canvas = document.createElement('canvas');
			canvas.width = targetWidth;
			canvas.height = targetHeight;
			const ctx = canvas.getContext('2d');

			// Calculate scale to cover the target area
			let scaledWidth;
			let scaledHeight;
			if (imageAspect > targetAspect) {
				// Image is wider than target - scale by height
				scaledWidth = targetHeight * imageAspect;
				scaledHeight = targetHeight;
			} else {
				// Image is taller than target - scale by width
				scaledWidth = targetWidth;
				scaledHeight = targetWidth / imageAspect;
			}

			// Use existing function to calculate image position from focal point
			const imagePosition = calcImagePositionForFocalPoint(
				naturalWidth, naturalHeight,
				targetWidth, targetHeight,
				focalX, focalY
			);
			const [xPercent, yPercent] = imagePosition.split(' ').map(p => parseFloat(p) / 100);

			// Convert percentage position to draw offset
			const overflowX = scaledWidth - targetWidth;
			const overflowY = scaledHeight - targetHeight;
			const drawX = -overflowX * xPercent;
			const drawY = -overflowY * yPercent;

			// Draw the scaled and cropped image
			ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);

			// Convert to JPEG blob with quality reduction to stay within file size limit
			const generateBlob = (quality) => {
				return new Promise((res) => {
					canvas.toBlob(blob => res(blob), 'image/jpeg', quality);
				});
			};

			try {
				let quality = 0.92;
				let blob = await generateBlob(quality);

				// Reduce quality until file size is within limit
				while (blob && blob.size > maxSize && quality > 0.1) {
					quality -= 0.1;
					blob = await generateBlob(quality);
				}

				if (blob) {
					resolve(blob);
				} else {
					reject(new Error('Failed to generate image blob'));
				}
			} catch (err) {
				reject(err);
			}
		};
		img.src = heroStillUrl;
	});
}

const maxFileSize = {
	'image/*': 2 * 1024 * 1024,
	'video/*': 6 * 1024 * 1024,
};
const fileSizeError = (file) => {
	const max = maxFileSize[file.type] || maxFileSize[`${file.type.split('/')[0]}/*`];
	if (max && file.size > max) {
		return `File is too large. Max size for this file type is ${+(max / 1024 / 1024).toFixed(1)}MB.`;
	}
	return null;
};

class Upload extends EventTarget {
	progress = 0;
	error = null;
	processed = false;

	constructor(file, onProcessed = null) {
		super();
		this.id = file.id || crypto.randomUUID();
		this.name = file.name;
		this.onProcessed = onProcessed;
	}

	dispatchUpdate() {
		this.dispatchEvent(new Event('update'));
	}

	setProgress({ loaded, total }) {
		this.progress = loaded / total;
		this.dispatchUpdate();
	}
	setProcessed(value, response) {
		this.processed = value;
		if (this.onProcessed && value === true) {
			this.onProcessed(response);
		}
		this.dispatchUpdate();
	}
	setError(value) {
		this.error = value;
		this.dispatchUpdate();
	}
}

export const upload = (file, opts) => {
	const upload = new Upload(file, opts.onProcessed);
	const fileSizeErr = fileSizeError(file);
	if (fileSizeErr) {
		upload.setError(fileSizeErr);
		return upload;
	}

	upload.promise = uploadRaw(file, upload, opts);
	return upload;
};

export const uploadRaw = async (file, upload, opts) => {
	const formData = new FormData();
	formData.append('file', file, file.name);
	formData.append('optimizeImage', true);
	if (opts.sectionId) formData.append('sectionId', opts.sectionId);
	if (opts.movieId) formData.append('movieId', opts.movieId);
	if (opts?.replaceId) {
		formData.append('replaceId', opts?.replaceId);
	}
	try {
		const dimensions = await getImageDimensions(file);
		if (dimensions) {
			formData.append('originalWidth', dimensions.width);
			formData.append('originalHeight', dimensions.height);
		}
	} catch {}

	try {
		const response = await post(
			'media/upload/single',
			formData,
			{ engine: xhr, asFormData: true, onUploadProgress: upload.setProgress.bind(upload) }
		);
		upload.setProcessed(true, response);
		upload.mediaId = response.id; // for cropper media assignment
	} catch (err) {
		if (err?.code === 413) {
			upload.setError('File is too large. Maximum size is 10MB.');
		} else {
			upload.setError('Error while uploading file.');
		}
		// throw err;
	}
};

function removeAllMediaReferences(object, url) {
	if (!object || typeof object !== 'object') {
		return object;
	}
	if (Array.isArray(object)) {
		// On arrays: if the url is a plain string, just remove the array element completely
		return object.filter(e => e !== url).map(e => removeAllMediaReferences(e, url));
	}
	// On objects: make the value blank but keep the key
	for (const key in object) {
		if (object[key] === url) {
			object[key] = '';
		} else {
			object[key] = removeAllMediaReferences(object[key], url);
		}
	}
	return object;
}

export const deleteMedia = async (id, options) => {
	// get media details before media entry is deleted
	const media = await getAllMedia();
	const mediaData = media.find(m => m.id === id);
	const { force, ...passedOptions } = options || {};
	await request(`/media/${id}`, {
		method: 'DELETE',
		qs: { force: !!force },
		...passedOptions,
	});
	mediaData.movie.forEach(async m => {
		// remove references to this media from any movies it's used in
		const data = jsonPointer.get(Store.get().data, `/movies/${m.id}/content`).toJS();
		const previous = JSON.stringify(data);
		removeAllMediaReferences(data, mediaData.s3_url);
		if (JSON.stringify(data) !== previous) {
			jsonPointer.setDeep(Store.get().data, `/movies/${m.id}/content`, data);
			await save(`/movies/${m.id}`);
		}
	});
	toast('File deleted!', { icon: 'delete' });
	Store.emit('UPDATE_MEDIA');
};

export class MediaInUseError extends Modal {
	title = 'Warning: media file in use';

	onItemLinkClick(e) {
		e.target.closest('dialog').close();
	}

	renderList(title, items, itemsOnly) {
		if (!items.length) return null;
		const renderedItems = items.map(item => (
			<li key={item.id}>
				<a href={item.link} onClick={this.onItemLinkClick}>{item.name}</a>
			</li>
		));
		if (itemsOnly) return renderedItems;
		return (
			<li key={title}>
				<strong>{title}:</strong>
				<ul>{renderedItems}</ul>
			</li>
		);
	}

	renderContent() {
		const movies = this.props.movie || [];
		const sections = this.props.section || [];
		const included = [
			movies.length && `movie${movies.length !== 1 ? 's' : ''}`,
			sections.length && `section${sections.length !== 1 ? 's' : ''}`,
		].filter(Boolean);
		const inUseBy = !included.length ? 'by a movie or page' : `in the following ${included.join(' and ')}`;
		return (
			<div class={this.c('content')}>
				<p>{`This file is in use ${inUseBy}, please replace it before deleting the file${included.length ? ':' : '.'}`}</p>
				<ul>
					{this.renderList('Movies', movies.map(m => ({ name: m.content?.title || m.title, link: `#movies/${m.movie_id}/media` })), included.length === 1)}
					{this.renderList('Sections', sections.map(s => ({ name: s.name, link: `#pages/${s.slug}/edit` })), included.length === 1)}
				</ul>
			</div>
		);
	}

	renderActions() {
		return (
			<div class={this.c('actions')}>
				<Button type="submit" value="CANCEL">Cancel</Button>
				<Button danger type="submit" value="DELETE" onClick={() => deleteMedia(this.props.id, { force: true })}>Delete Anyways</Button>
				{/* Button to force delete? */}
			</div>
		);
	}
}
export async function showMediaInUseError(details, id) {
	return await showModal(MediaInUseError, { ...details, id });
}